// Floating uPlot panel for 1-D reduction profiles (Stage 6).
//
// One window per Reduction id. Registers with the WindowRegistry (Stage 1)
// so drag / resize / position persistence come for free. Subscribes to the
// reduction-cache module so a recompute re-paints the chart without a full
// rebuild.
//
// Module pattern: the WindowRegistry is captured once at install time via
// `installReductionPlots(registry)`; callers (the Reductions pane) then
// call `openReductionPlot(id)` without threading the registry through.
// Mirrors how the chrome / panels drivers grab globals at install time.

import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';
import { store, params } from '@store/instance';
import { INTENTS } from '@store/intents';
import { getResult, onResult } from '../../reductions/cache';
import type { ReductionId } from '@store/schema';
import type { ReductionResult } from '../../reductions/types';
import type { WindowHandle, WindowRegistry } from '../windowManager';

interface WindowEntry {
    handle: WindowHandle;
    panel: HTMLElement;
    headerLabel: HTMLElement;
    body: HTMLElement;
    plot: uPlot | null;
    offCache: () => void;
    offParams: () => void;
    offResize: () => void;
}

const windows = new Map<ReductionId, WindowEntry>();
let registry: WindowRegistry | null = null;

/** Capture the WindowRegistry. Called once from initUI. */
export function installReductionPlots(reg: WindowRegistry): () => void {
    registry = reg;
    // Close any open plot when its underlying reduction is deleted.
    const offDelete = store.onIntent(INTENTS.REDUCTION_DELETE, (p) => {
        if (!p?.id) return;
        closeReductionPlot(p.id);
    });
    return () => {
        offDelete();
        for (const id of Array.from(windows.keys())) closeReductionPlot(id);
        registry = null;
    };
}

/** Open or focus the floating plot window for `id`. No-op if registry isn't installed. */
export function openReductionPlot(id: ReductionId): void {
    if (!registry) {
        console.warn('[reductions/plotWindow] registry not installed; openReductionPlot dropped');
        return;
    }
    const existing = windows.get(id);
    if (existing) {
        existing.handle.show();
        existing.handle.raise();
        repaint(existing, id);
        return;
    }
    const entry = createWindow(id, registry);
    windows.set(id, entry);
    entry.handle.show();
    repaint(entry, id);
}

export function closeReductionPlot(id: ReductionId): void {
    const entry = windows.get(id);
    if (!entry) return;
    entry.offCache();
    entry.offParams();
    entry.offResize();
    if (entry.plot) entry.plot.destroy();
    entry.handle.dispose();
    entry.panel.remove();
    windows.delete(id);
}

// ─── window construction ───────────────────────────────────────────────────

function createWindow(id: ReductionId, reg: WindowRegistry): WindowEntry {
    const panel = document.createElement('div');
    panel.id = `reductions-plot-${id}`;
    panel.className = 'reductions-plot-panel';
    // No inline `display: none` — `.reductions-plot-panel { display: none }`
    // owns that, so the `.show` class can flip to `display: flex` via the
    // same-specificity rule below. Inline display would win against the class.
    panel.style.right = '20px';
    panel.style.bottom = '380px'; // sit above the console default by ~60px
    panel.style.width = '480px';
    panel.style.height = '260px';

    const grip = document.createElement('div');
    grip.className = 'mv-window-grip';
    grip.setAttribute('aria-hidden', 'true');
    panel.appendChild(grip);

    // Header (drag handle + breadcrumb + close button)
    const header = document.createElement('div');
    header.className = 'reductions-plot-header';
    const headerLabel = document.createElement('div');
    headerLabel.className = 'reductions-plot-title';
    headerLabel.textContent = '…';
    header.appendChild(headerLabel);

    const pinBtn = document.createElement('button');
    pinBtn.type = 'button';
    pinBtn.className = 'reductions-plot-pin';
    pinBtn.title = 'Toggle pin / follow timestep';
    pinBtn.setAttribute('aria-label', 'Toggle pin / follow timestep');
    pinBtn.innerHTML = '<i class="fas fa-thumbtack" aria-hidden="true"></i>';
    pinBtn.addEventListener('click', () => {
        const cur = params.reductions[id];
        if (!cur) return;
        const next = cur.timestepPolicy === 'pin' ? 'follow' : 'pin';
        store.dispatch(INTENTS.REDUCTION_UPDATE, { id, patch: { timestepPolicy: next } });
    });
    header.appendChild(pinBtn);

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'reductions-plot-close';
    closeBtn.setAttribute('aria-label', 'Close plot');
    closeBtn.innerHTML = '<i class="fas fa-times" aria-hidden="true"></i>';
    header.appendChild(closeBtn);
    panel.appendChild(header);

    const body = document.createElement('div');
    body.className = 'reductions-plot-body';
    panel.appendChild(body);

    const resize = document.createElement('div');
    resize.className = 'reductions-plot-resize';
    resize.dataset['noDrag'] = '';
    panel.appendChild(resize);

    document.body.appendChild(panel);

    const handle = reg.register({
        id: `reductions-plot-${id}`,
        panel,
        header,
        resize,
        closeBtn,
        minWidth: 320,
        minHeight: 200,
    });

    // Recompute → repaint chart + breadcrumb.
    const offCache = onResult(id, () => {
        const entry = windows.get(id);
        if (entry) repaint(entry, id);
    });

    // Label / op / domain changes don't trigger a recompute via cache (only the
    // result might be identical), but the breadcrumb text must refresh. Listen
    // for params changes on this reduction's slot.
    const offParams = store.subscribe(`params.reductions.${id}`, () => {
        const entry = windows.get(id);
        if (entry) refreshBreadcrumb(entry, id);
    });

    const ro = new ResizeObserver(() => {
        const entry = windows.get(id);
        if (entry && entry.plot) {
            entry.plot.setSize({ width: body.clientWidth, height: body.clientHeight });
        }
    });
    ro.observe(body);
    const offResize = (): void => ro.disconnect();

    return { handle, panel, headerLabel, body, plot: null, offCache, offParams, offResize };
}

// ─── repaint ───────────────────────────────────────────────────────────────

function repaint(entry: WindowEntry, id: ReductionId): void {
    refreshBreadcrumb(entry, id);
    const result = getResult(id);
    if (!result || result.kind !== 'profile') {
        entry.body.textContent = '';
        if (entry.plot) { entry.plot.destroy(); entry.plot = null; }
        const note = document.createElement('div');
        note.className = 'reductions-plot-empty';
        note.textContent = result ? summariseNonProfile(result) : 'computing…';
        entry.body.appendChild(note);
        return;
    }

    // Remove the empty-state note if present.
    if (!entry.plot) {
        entry.body.textContent = '';
        const w = Math.max(120, entry.body.clientWidth);
        const h = Math.max(80, entry.body.clientHeight);
        // uPlot renders to canvas — `getComputedStyle` resolves CSS vars to
        // real color strings the canvas 2D API can consume. Passing
        // `var(--magviz-text)` directly would render axes/ticks as black,
        // which is invisible on the dark panel background.
        const cs = getComputedStyle(entry.panel);
        const text = (cs.getPropertyValue('--magviz-text').trim() || '#cfdaf2');
        const accent = (cs.getPropertyValue('--magviz-accent').trim() || '#6db1ff');
        const grid = 'rgba(255,255,255,0.08)';
        const opts: uPlot.Options = {
            width: w,
            height: h,
            cursor: { drag: { setScale: false }, focus: { prox: 12 } },
            scales: { x: { time: false } },
            axes: [
                {
                    stroke: text,
                    grid: { stroke: grid },
                    ticks: { stroke: text },
                    font: '11px var(--magviz-font-family, system-ui, sans-serif)',
                    label: result.axis,
                    labelFont: 'bold 11px var(--magviz-font-family, system-ui, sans-serif)',
                },
                {
                    stroke: text,
                    grid: { stroke: grid },
                    ticks: { stroke: text },
                    font: '11px var(--magviz-font-family, system-ui, sans-serif)',
                    label: result.op,
                    labelFont: 'bold 11px var(--magviz-font-family, system-ui, sans-serif)',
                    size: 56,
                },
            ],
            series: [
                { label: result.axis },
                {
                    label: result.op,
                    stroke: accent,
                    width: 1.5,
                    points: { show: false },
                },
            ],
        };
        entry.plot = new uPlot(opts, toData(result), entry.body);
    } else {
        entry.plot.setData(toData(result));
    }
}

function toData(r: Extract<ReductionResult, { kind: 'profile' }>): uPlot.AlignedData {
    // uPlot's typed surface expects readonly number[]; Float32Array is iterable but
    // not assignable to that signature, so cast at the boundary. The contents are
    // numeric — the cast is a Float32Array→number[] view, not a semantic change.
    return [r.coords as unknown as number[], r.values as unknown as number[]];
}

function summariseNonProfile(r: ReductionResult): string {
    switch (r.kind) {
        case 'scalar': return `0-D scalar = ${r.value} (n=${r.count})`;
        case 'empty': return 'No cells in the selected domain.';
        case 'unsupported': return `Cannot plot: ${r.reason}`;
        case 'profile': return ''; // unreachable
    }
}

function refreshBreadcrumb(entry: WindowEntry, id: ReductionId): void {
    const r = params.reductions[id];
    if (!r) {
        entry.headerLabel.textContent = '(deleted)';
        return;
    }
    const survivor = (['x', 'y', 'z'] as const).find((a) => !r.axes.includes(a));
    const domain =
        r.domain === 'full' ? 'Full'
        : params.selections[r.domain]?.label ?? '(deleted selection)';
    const along = survivor ? ` along ${survivor}` : '';
    // t=N tag deferred to Stage 8 (when the timestep scrubber lands and exposes
    // the current step on params). Until then the breadcrumb omits it.
    const pinTag = r.timestepPolicy === 'pin' ? ' · pinned' : '';
    entry.headerLabel.textContent = `${r.op}(${r.field})${along} · Domain: ${domain}${pinTag}`;
    entry.panel.dataset['timestepPolicy'] = r.timestepPolicy;
}
